import React, { useContext, useEffect, useState } from 'react';
import { Button, InputNumber, message } from 'antd';
import { ShoppingCartOutlined, PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { LoginContext } from '../../../../context/loginContext';
import { CarritoContext } from '../../../../context/carritoContext';

/** Si esVistaInicioGrid: solo cards del listado en /home; en móvil texto "Agregar" y ancho seguro. */
const CAgregarCarrito = ({ articulo, esVistaInicioGrid = false }) => {
  const { user, isAuthenticated } = useContext(LoginContext);
  const carritoContext = useContext(CarritoContext);
  const navigate = useNavigate();
  const { loginWithRedirect } = useAuth0();

  const [mostrarControles, setMostrarControles] = useState(false);
  const [cantidad, setCantidad] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { agregarArticulo, restarArticulo, carrito } = carritoContext || {};
  const articuloId = articulo.articulo_empresa?.aem_id;

  // ✅ useEffect siempre se llama sin condicionales
  useEffect(() => {
    if (
      carritoContext &&
      carrito &&
      carrito.detalles &&
      articuloId
    ) {
      const existente = carrito.detalles.find(
        det => Number(det.dec_articulo) === Number(articuloId)
      );
      if (existente) {
        setCantidad(parseFloat(existente.dec_cantidad) || 0);
        setMostrarControles(true);
      } else {
        setCantidad(1);
        setMostrarControles(false);
      }
    }
  }, [carrito, articuloId, carritoContext]);

  const handleClickAgregar = async () => {
    if (!isAuthenticated) {
      loginWithRedirect();
      return;
    }

    try {
      setLoading(true);
      await agregarArticulo(articuloId, 1);
      setCantidad(1);
      setMostrarControles(true);
      message.success('Artículo agregado al carrito');
    } catch (error) {
      message.error('Error al agregar al carrito');
    } finally {
      setLoading(false);
    }
  };

  // Obtener el nombre del tipo de fraccionamiento desde el detalle o usar 'entero' por defecto
  const tipoUnidadNombre = articulo?.articulo_empresa?.tipo_unidad_nombre || 'Entero';
  const contenidoNeto = articulo?.articulo_empresa?.detalle?.contenido_neto 
    ? parseFloat(articulo.articulo_empresa.detalle.contenido_neto) 
    : null;
  const esFraccionado = tipoUnidadNombre.toLowerCase() === 'fraccionado' && contenidoNeto;
  
  // Obtener unidad de medida para convertir gramos a kilos
  const unidadMedida = articulo?.articulo_empresa?.detalle?.ums_nombre || '';
  const esGramos = (unidadMedida.toLowerCase().includes('gramo') || unidadMedida.toLowerCase().includes('g')) 
    && !unidadMedida.toLowerCase().includes('kilo');
  const esKilos = unidadMedida.toLowerCase().includes('kilo') || unidadMedida.toLowerCase().includes('kg');
  
  // Función para convertir cantidad a kilos si es necesario
  const convertirAKilos = (cant) => {
    if (esFraccionado && contenidoNeto) {
      if (esGramos) {
        // Si está en gramos: convertir a kilos dividiendo por 1000
        const totalKilos = cant / 1000;
        return parseFloat(totalKilos.toFixed(2));
      } else if (esKilos) {
        // Si ya está en kilos: mostrar directamente con 1 decimal
        return parseFloat(cant.toFixed(1));
      }
    }
    return cant;
  };
  
  // Obtener el valor a mostrar
  const cantidadMostrar = esFraccionado && (esGramos || esKilos)
    ? convertirAKilos(cantidad) 
    : cantidad;
  
  // Determinar si mostrar el sufijo "kg"
  const mostrarKg = esFraccionado && (esGramos || esKilos);

  const handleSumar = async () => {
    try {
      setLoading(true);
      await agregarArticulo(articuloId, 1);
      // El backend maneja la cantidad según tipo_unidad
      // Actualizamos la cantidad desde el carrito después de la actualización
      if (carritoContext?.fetchCarrito) {
        await carritoContext.fetchCarrito();
      }
    } catch (error) {
      message.error('Error al agregar al carrito');
    } finally {
      setLoading(false);
    }
  };

  const handleRestar = async () => {
    const cantidadMinima = esFraccionado ? contenidoNeto : 1;
    if (cantidad > cantidadMinima) {
      try {
        setLoading(true);
        await restarArticulo(articuloId);
        // El backend maneja la cantidad según tipo_unidad
        // Actualizamos la cantidad desde el carrito después de la actualización
        if (carritoContext?.fetchCarrito) {
          await carritoContext.fetchCarrito();
        }
      } catch (error) {
        message.error('Error al restar del carrito');
      } finally {
        setLoading(false);
      }
    }
  };

  if (!carritoContext || (user && user?.tiu_descripcion !== 'Cliente')) return null;

  const compactoListado = esVistaInicioGrid && isMobile;
  /** Detalle (y resto fuera del grid inicio): en móvil, fila cantidad centrada */
  const centrarControlesDetalle =
    mostrarControles && isMobile && !compactoListado;
  const anchoContenedor =
    compactoListado || centrarControlesDetalle ? '100%' : 'auto';
  const justificarControles =
    (compactoListado && mostrarControles) || centrarControlesDetalle
      ? 'center'
      : 'flex-start';

  const ALTURA_CONTROLES_DETALLE = 44;

  const botonesMasAnchosDetalle =
    mostrarControles && !esVistaInicioGrid
      ? {
          minWidth: 52,
          width: 52,
          height: ALTURA_CONTROLES_DETALLE,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingInline: 0,
        }
      : undefined;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compactoListado ? 6 : 8,
        width: anchoContenedor,
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
        justifyContent: justificarControles,
        flexWrap: compactoListado && mostrarControles ? 'wrap' : 'nowrap'
      }}
    >
      {!mostrarControles ? (
        <Button
          icon={<ShoppingCartOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleClickAgregar();
          }}
          loading={loading}
          style={{
            backgroundColor: '#01634C',
            borderColor: '#01634C',
            color: '#fff',
            color: '#fff',
            fontWeight: 'bold',
            borderRadius: '12px',
            padding: compactoListado ? '0 8px' : '0 16px',
            height: compactoListado ? 36 : 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            width: compactoListado ? '100%' : isMobile ? 'calc(100% - 16px)' : 'auto',
            maxWidth: compactoListado ? '100%' : isMobile ? 'calc(100% - 16px)' : 'none',
            fontSize: compactoListado ? 13 : undefined
          }}
        >
          {compactoListado ? 'Agregar' : 'Agregar al carrito'}
        </Button>
      ) : (
        <>
          <Button
            icon={<MinusOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleRestar();
            }}
            loading={loading}
            style={botonesMasAnchosDetalle}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              height: !esVistaInicioGrid ? ALTURA_CONTROLES_DETALLE : undefined,
            }}
          >
            <InputNumber
              min={esFraccionado ? contenidoNeto : 1}
              value={cantidadMostrar}
              readOnly
              controls={!esVistaInicioGrid ? false : undefined}
              className={!esVistaInicioGrid ? 'input-cantidad-detalle' : undefined}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: mostrarKg ? 80 : 60,
                textAlign: 'center',
                pointerEvents: 'none',
                textAlignLast: 'center',
                ...(!esVistaInicioGrid && {
                  height: ALTURA_CONTROLES_DETALLE,
                }),
              }}
              precision={mostrarKg ? (esKilos ? 1 : 2) : 0}
            />
            {mostrarKg && (
              <span style={{ fontSize: '0.9rem', color: '#666' }}>kg</span>
            )}
          </div>
          <Button
            icon={<PlusOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleSumar();
            }}
            loading={loading}
            style={botonesMasAnchosDetalle}
          />
        </>
      )}
    </div>
  );
};

export default CAgregarCarrito;